import { useEffect, useMemo, useRef, useState } from "react";
import { COUNTRIES, TOP_CODES, findByCode, searchCountries } from "../lib/countries";

/**
 * Country code dropdown + phone input.
 * Stores phone as full international format: +<dial><digits>.
 *
 * Props:
 *  - value: full phone string e.g. "+919876543210" (or partial). Default "+91 ".
 *  - onChange(fullPhone, { country, digits, isValid })
 *  - hasError: boolean → show red border
 */
export default function CountryPhoneInput({ value = "", onChange, hasError = false, testid = "phone" }) {
  const initial = parseValue(value);
  const [country, setCountry] = useState(initial.country);
  const [digits, setDigits] = useState(initial.digits);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);

  // Emit upstream
  useEffect(() => {
    const isValid = country.lengths.includes(digits.length);
    const full = digits ? `+${country.dial}${digits}` : "";
    onChange?.(full, { country, digits, isValid });
  }, [country, digits]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setShowSearch(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const top = useMemo(() => TOP_CODES.map(findByCode), []);
  const rest = useMemo(
    () =>
      [...COUNTRIES]
        .filter((c) => !TOP_CODES.includes(c.code))
        .sort((a, b) => a.name.localeCompare(b.name)),
    []
  );
  const filtered = useMemo(() => (query ? searchCountries(query) : null), [query]);

  const selectCountry = (c) => {
    setCountry(c);
    setOpen(false);
    setShowSearch(false);
    setQuery("");
  };

  const onDigitChange = (e) => {
    const d = e.target.value.replace(/\D/g, "").slice(0, 14);
    setDigits(d);
  };

  return (
    <div ref={wrapperRef} className="cph-wrapper">
      <button
        type="button"
        className={`cph-code ${hasError ? "cph-err" : ""}`}
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testid}-code`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="cph-flag" aria-hidden>{country.flag}</span>
        <span className="cph-dial">+{country.dial}</span>
        <span className="cph-caret" aria-hidden>&#9662;</span>
      </button>
      <input
        type="tel"
        inputMode="tel"
        className={`pf-input cph-input ${hasError ? "pf-input-err" : ""}`}
        placeholder="98765 43210"
        value={digits}
        onChange={onDigitChange}
        data-testid={`${testid}-number`}
      />

      {open && (
        <div className="cph-dropdown" role="listbox" data-testid={`${testid}-dropdown`}>
          {showSearch && (
            <div className="cph-search-row">
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country or code..."
                className="cph-search"
                autoFocus
                data-testid={`${testid}-search`}
              />
            </div>
          )}

          {/* Filtered (search active) */}
          {filtered ? (
            <ul className="cph-list">
              {filtered.length === 0 && <li className="cph-empty">No matches</li>}
              {filtered.map((c) => (
                <CountryRow key={c.code} c={c} onClick={() => selectCountry(c)} />
              ))}
            </ul>
          ) : (
            <>
              {/* Top 11: 10 popular + Other */}
              <ul className="cph-list">
                {top.map((c) => (
                  <CountryRow key={c.code} c={c} onClick={() => selectCountry(c)} active={c.code === country.code} />
                ))}
                <li
                  className="cph-row cph-other"
                  onClick={() => {
                    setShowSearch(true);
                    setTimeout(() => searchRef.current?.focus(), 0);
                  }}
                  role="option"
                  aria-selected="false"
                  data-testid={`${testid}-other`}
                >
                  <span className="cph-flag" aria-hidden>🌐</span>
                  <span className="cph-row-main">
                    <span>Other</span>
                    <span className="cph-row-sub">Search below</span>
                  </span>
                </li>
              </ul>
              <div className="cph-divider" />
              <ul className="cph-list">
                {rest.map((c) => (
                  <CountryRow key={c.code} c={c} onClick={() => selectCountry(c)} active={c.code === country.code} />
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CountryRow({ c, onClick, active }) {
  return (
    <li
      className={`cph-row ${active ? "cph-row-active" : ""}`}
      onClick={onClick}
      role="option"
      aria-selected={!!active}
    >
      <span className="cph-flag" aria-hidden>{c.flag}</span>
      <span className="cph-row-main">
        <span>{c.name}</span>
        <span className="cph-row-dial">+{c.dial}</span>
      </span>
    </li>
  );
}

// --- helpers ---
function parseValue(v) {
  // Accept "+91 98765 43210", "+919876543210", "919876543210", or empty/default.
  const cleaned = (v || "").replace(/\s/g, "");
  if (!cleaned || cleaned === "+") {
    return { country: findByCode("IN"), digits: "" };
  }
  const noPlus = cleaned.replace(/^\+/, "");
  // Try matching the longest dial code first
  const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  for (const c of sorted) {
    if (noPlus.startsWith(c.dial)) {
      return { country: c, digits: noPlus.slice(c.dial.length) };
    }
  }
  return { country: findByCode("IN"), digits: noPlus };
}
